import "./styles.css";

import Grid from "@material-ui/core/Grid";
import { CommodityInput } from "components/inputs/CommodityInput";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { CountryISOCodeInput } from "components/inputs/CountryISOCodeInput";
import { OrderReferenceInput } from "components/inputs/OrderReferenceInput";
import TerminalDatesInput from "components/inputs/TerminalDatesInput";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import { ExternalTerminalConnectionStatus } from "entities/ExternalTerminalConnectionStatus";
import union from "lodash/union";
import React from "react";
import {
	ReferenceInput,
	TextInput,
	maxLength,
	useDataProvider,
	usePermissions,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { ColdStorageSection } from "resources/orders/forms/ColdStorageSection";

import { formatPhoneNumber } from "../../../../../components/fields/PhoneField";
import {
	FormGroupGrid,
	GridForm,
	GridInputMultiChanges as GridInput,
	MultiChangesStateEnum,
	isMultiDiff,
} from "../../../../../components/GridForm";
import {
	CompanySelect,
	DateInput,
	NumberInputEx,
	PhoneInput,
	TimeInput,
} from "../../../../../components/inputs";
import { PortInput } from "../../../../../components/inputs/PortInput";
import { SelectTextInput } from "../../../../../components/inputs/SelectTextInput";
import {
	SelectDisabledInput,
	SelectUppercaseInput,
} from "../../../../../components/inputs/SelectUppercaseInput";
import { YesNoInput } from "../../../../../components/inputs/YesNoInput";
import {
	DriverRoles,
	OrderNormalStatuses,
	OrderService,
	OrderStatus,
	OrderStatusTransporterEditOrder_1,
	OrderStatusTransporterEditOrder_2,
	UserRole,
	perEligibleServices,
} from "../../../../../entities";
import HideableOrderField from "../../../../../entities/HideableOrderField";
import { useTerminalFieldVisibility } from "../../../../../hooks/useTerminalFieldVisibility";
import { isNullUndEmpty } from "../../../../../utils/general";
import { validatePlaceLength } from "../../../../../utils/validation/order";
import { required } from "../../../../../utils/validation/required";
import { requiredIfNoFieldValue } from "../../../../../utils/validation/requiredIfNoFieldValue";
import { LoadingOrderInput } from "../../../forms/inputs/LoadingOrderInput";
import { PrintedDocsInput } from "../../../forms/inputs/PrintedDocsInput";
import { ServiceInput } from "../../../forms/inputs/ServiceInput";
import { TransporterInput } from "../../../forms/inputs/TransporterInput";
import { TripNumberInput } from "../../../forms/inputs/TripNumberInput";
import { PlumSealSection } from "../../../forms/PlumSealSection";
import { useXPartyChange } from "../../../forms/shared";
import {
	DangerTextInputForInternalNotes,
	ETAETDFormGroup,
	etaEtdPorts,
	useDriverChange,
	useEtaEtdSync,
} from "../../../forms/shared";

const rowGroupStyle = {
	borderBottom: "2px solid #bdbdbd",
	paddingBottom: 4,
	marginBottom: 4,
};

export const ETAETDBlock = ({
	isAddMode,
	prefix,
	canEditOnlyTime,
	title,
	requiredMulti,
	isFieldHidden,
	loadingOrderDisable,
	...props
}) => {
	const { permissions } = usePermissions();
	const { data: terminals } = useTerminalsContext();
	const { values } = useFormState();
	const byTerminalFilter = { terminal_id: [values["terminal_id"]] };
	const handleEtaEtdSync = useEtaEtdSync(prefix);
	const handleDriverChange = useDriverChange(prefix);
	const [handle4thPartyChangeOnTruck, handle4thPartyChangeOnTrailer, handleXPartyChangeOnDriver] =
		useXPartyChange(prefix);

	if (!permissions) return null;
	const userId = permissions.id;
	const userCompanyId = permissions.company_id;

	if (
		!(
			isAddMode ||
			userCompanyId === values[`${prefix}_3rd_party`] ||
			userCompanyId === values[`${prefix}_4th_party`] ||
			(userCompanyId === values["company_id"] && userId === values[`${prefix}_driver_id`])
		)
	) {
		return <Grid item {...props} />;
	}

	const isManager =
		permissions.role === UserRole.transporter_manager ||
		permissions.role === UserRole.customer_and_transporter_manager;
	const isEmployee =
		permissions.role === UserRole.transporter_employee ||
		permissions.role === UserRole.customer_and_transporter_employee;
	const isDriver =
		!isManager &&
		(permissions.role === UserRole.transporter_driver ||
			permissions.role === UserRole.customer_and_transporter_driver);
	const isTransporterSelected = !!values[`${prefix}_3rd_party`];
	const is4thPartySelected = !!values[`${prefix}_4th_party`];
	const isOwnCompany = values["company_id"] === userCompanyId;

	const is4thPartyUser = values[`${prefix}_4th_party`] === userCompanyId;

	const isDisabledTimeForEditing = isDriver
		? !((isTransporterSelected || isOwnCompany) && values[`${prefix}_driver_id`] === userId)
		: !(
				isTransporterSelected &&
				values[`${prefix}_3rd_party`] === userCompanyId &&
				values[`${prefix}_4th_party`] == null
		  );

	const isDisabledForUser = isDriver
		? !((is4thPartySelected || isOwnCompany) && values[`${prefix}_driver_id`] === userId)
		: !(is4thPartySelected && values[`${prefix}_4th_party`] === userCompanyId);

	const isDisabledForEditing = canEditOnlyTime || (isDisabledForUser && isDisabledTimeForEditing);
	const isColdStorageService = values.service === OrderService.coldStorage;
	const isIntoPlukkStorage = values.service === OrderService.intoPlukkStorage;

	const dateRequiredValueKeyPrefix = prefix === "eta" ? "etd" : "eta";
	const portKey = etaEtdPorts[prefix];
	const currentTransportFilter = { no_range: true, is_deleted: false };
	const currentTransportSort = { field: "name", order: "ASC" };
	if (isManager) {
		currentTransportFilter.company_id = userCompanyId;
	}

	const currentDriverFilter = {
		no_range: true,
		company_id: userCompanyId,
		is_blocked: false,
		is_deleted: false,
	};
	const currentDriverSort = { field: "first_name", order: "ASC" };
	const placePrefix = prefix === "eta" ? "from" : "to";

	const handle4thPartyChange = (e) => {
		handle4thPartyChangeOnTruck(e);
		handle4thPartyChangeOnTrailer(e);
		handleXPartyChangeOnDriver(e);
	};

	return (
		<ETAETDFormGroup {...props} prefix={prefix}>
			{/* Row 1: 4th party, Date, Time, Slot time, Port */}
			<FormGroupGrid style={rowGroupStyle}>
				{!is4thPartyUser && (
					<GridInput
						xs={12}
						sm={5}
						md={5}
						component={TransporterInput}
						source={`${prefix}_4th_party`}
						prefix={prefix}
						excludeIds={[values.company_id, values[`${prefix}_3rd_party`]]}
						disabled={!values.company_id}
						onChange={handle4thPartyChange}
						hidden={isFieldHidden(`${prefix}_4th_party`)}
					/>
				)}

				<GridInput xs={6} sm={3}>
					<TerminalDatesInput
						component={DateInput}
						source={`${prefix}_date`}
						disabled={isDisabledForEditing}
						validate={[
							isIntoPlukkStorage
								? requiredIfNoFieldValue(`${dateRequiredValueKeyPrefix}_date`)
								: required(),
						]}
						onChange={handleEtaEtdSync}
					/>
				</GridInput>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput
						component={TimeInput}
						source={`${prefix}_time`}
						disabled={isDisabledTimeForEditing}
					/>
				</GridInput>
				<GridInput xs={6} sm={3} component={TextInput} source={`${prefix}_slot_time`} disabled />
				<GridInput
					xs={6}
					sm={3}
					component={PortInput}
					source={portKey}
					filter={byTerminalFilter}
					disabled
				/>
			</FormGroupGrid>

			{/* Row 2: Place, Seal, Seal number, Trip number, Loading order */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={12}
					md={4}
					isDefaultTextMode
					disabled={isDisabledForEditing}
					component={SelectTextInput}
					sourceText={`place_${placePrefix}`}
					reference="external-terminal-links"
					filter={{
						status: ExternalTerminalConnectionStatus.connected,
						own_terminal_guid: [terminals[values["terminal_id"]].guid],
					}}
					sort={{ field: "external_name", order: "ASC" }}
					validate={[validatePlaceLength]}
					disabledSelect={prefix === "eta"}
					allowEmpty
					resettable
					hidden={isFieldHidden(`place_${placePrefix}`)}
				/>
				<GridInput
					xs={12}
					md={4}
					disabled={isDisabledForEditing}
					component={CountryISOCodeInput}
					source={`place_${placePrefix}_country_code`}
					hidden={isFieldHidden(`place_${placePrefix}_country_code`)}
				/>
				<GridInput
					xs={6}
					md={2}
					component={YesNoInput}
					source={prefix === "eta" ? "seal" : "plumb"}
					defaultValue={false}
					disabled
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL : HideableOrderField.PLUMB)}
				/>
				<GridInput
					xs={6}
					md={3}
					component={TextInput}
					source={prefix === "eta" ? "seal_number" : "plumb_number"}
					disabled
					resettable
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL_NUMBER : HideableOrderField.PLUMB_NUMBER)}
				/>
				<GridInput
					xs={6}
					md={2}
					component={TripNumberInput}
					source={prefix === "eta" ? "incoming_trip_number" : "outgoing_trip_number"}
					disabled
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.INCOMING_TRIP_NUMBER : HideableOrderField.OUTGOING_TRIP_NUMBER)}
				/>
				<GridInput
					xs={6}
					md={2}
					component={LoadingOrderInput}
					source={prefix === "eta" ? "unloading_sequence" : "loading_sequence"}
					disabled={prefix === "eta" ? true : loadingOrderDisable}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.UNLOADING_SEQUENCE : HideableOrderField.LOADING_SEQUENCE)}
				/>
			</FormGroupGrid>

			{/* Row 3: Truck, Trailer, Driver, Phone */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={12}
					sm={6}
					md={6}
					lg={3}
					xl={3}
					component={SelectTextInput}
					label={"resources.orders.fields." + prefix + "_truck"}
					sourceText={`${prefix}_truck`}
					sourceSelect={`${prefix}_truck_id`}
					prefix={prefix}
					reference="trucks"
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					resettable
					hidden={isFieldHidden(`${prefix}_truck`)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={6}
					lg={3}
					xl={3}
					component={SelectTextInput}
					label={"resources.orders.fields." + prefix + "_trailer"}
					sourceText={`${prefix}_trailer`}
					sourceSelect={`${prefix}_trailer_id`}
					reference="trailers"
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					resettable
					hidden={isFieldHidden(`${prefix}_trailer`)}
				/>
				{(isManager || isEmployee) && (
					<GridInput
						xs={12}
						sm={6}
						md={6}
						lg={3}
						xl={3}
						component={SelectTextInput}
						sourceText={`${prefix}_driver`}
						sourceSelect={`${prefix}_driver_id`}
						reference="drivers"
						optionText={(record) =>
							`${record.first_name} ${record.last_name} | ${formatPhoneNumber(record.phone)}`
						}
						filter={currentDriverFilter}
						sort={currentDriverSort}
						disabled={isDisabledForEditing}
						validate={[maxLength(64)]}
						onChange={handleDriverChange}
						hidden={isFieldHidden(`${prefix}_driver`)}
					/>
				)}
				{!isDisabledForEditing && (
					<GridInput
						xs={12}
						sm={6}
						md={6}
						lg={3}
						xl={3}
						component={PhoneInput}
						source={`${prefix}_driver_phone`}
						disabled={isDisabledForEditing}
						hidden={isFieldHidden(`${prefix}_driver_phone`)}
					/>
				)}
				{prefix === "etd" && (
					<GridInput
						xs={6}
						sm={3}
						lg={2}
						xl={1}
						component={YesNoInput}
						source="is_doc_for_nfsa"
						defaultValue={false}
						disabled
						hidden={isFieldHidden(HideableOrderField.IS_DOC_FOR_NFSA)}
					/>
				)}
			</FormGroupGrid>

			{/* Row 4: Goods & Handling (ETA - all disabled) */}
			<FormGroupGrid>
				{prefix === "eta" ? (
					<>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							hidden={
								values.service === OrderService.reloadCarCar ||
								isFieldHidden(HideableOrderField.COOLER_TYPE_ID)
							}
							disabled
						>
							<CoolerTypeSelect filter={{ order_service: values.service }} disabled uppercase />
						</GridInput>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="pallets"
							min={0}
							max={100}
							step={10}
							disabled
							hidden={isFieldHidden(HideableOrderField.PALLETS)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="boxes"
							min={0}
							max={100}
							step={5}
							disabled
							hidden={isFieldHidden(HideableOrderField.BOXES)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							type="float"
							digitsCount={2}
							source="kilos"
							min={0}
							max={1000}
							step={100}
							disabled
							hidden={isFieldHidden(HideableOrderField.KILOS)}
						/>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							component={CommodityInput}
							source="commodity_id"
							disabled
							hidden={isFieldHidden(HideableOrderField.COMMODITY_ID)}
						/>
					</>
				) : (
					<>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={PrintedDocsInput}
							source="printed_doc"
							defaultValue={false}
							disabled
							hidden={isFieldHidden(HideableOrderField.PRINTED_DOC)}
						/>
					</>
				)}
			</FormGroupGrid>
		</ETAETDFormGroup>
	);
};

const getDiffKeys = (obj1, obj2) => {
	const keys = union(Object.keys(obj1), Object.keys(obj2));
	return keys.filter((x) => obj1[x] !== obj2[x]);
};

const fieldRelation = {
	terminal_id: ["port_in", "prot_out", "storage_place_id", "area_id"],
	eta_3rd_party: [
		"eta_driver_id",
		"eta_truck_id",
		"eta_trailer_id",
		"eta_driver",
		"eta_truck",
		"eta_trailer",
	],
	etd_3rd_party: [
		"etd_driver_id",
		"etd_truck_id",
		"etd_trailer_id",
		"etd_driver",
		"etd_truck",
		"etd_trailer",
	],
	company_id: ["eta_3rd_party", "etd_3rd_party"],
};
fieldRelation["company_id"] = [
	...fieldRelation["company_id"],
	...fieldRelation["eta_3rd_party"],
	...fieldRelation["etd_3rd_party"],
];

export const FormTransporter = ({ isAddMode, initRecordData, ...props }) => {
	const form = useForm();

	const { permissions } = usePermissions();
	const { values, initialValues } = useFormState();
	const { isFieldHidden } = useTerminalFieldVisibility(values?.terminal_id);
	const translate = useTranslate();
	const dataProvider = useDataProvider();
	const [canEditOnlyTime, setCanEditOnlyTime] = React.useState(false);

	const [loadingOrderDisable, setLoadingOrderDisable] = React.useState(true);
	const [recordData, setRecordData] = React.useState(initRecordData);
	const [enabledFields, setEnabledFields] = React.useState();
	const isColdStorageService =
		initRecordData?.multiValues["service"]?.length > 0 &&
		initRecordData.multiValues["service"].every((x) => x === OrderService.coldStorage);

	React.useEffect(() => {
		if (permissions) {
			if (DriverRoles.indexOf(permissions.role) !== -1) {
				dataProvider.getOne("drivers", { id: permissions.id }).then((result) => {
					setCanEditOnlyTime(result.data.can_edit_only_time);
				});
			}

			if (initRecordData && initRecordData.multiValues && initRecordData.multiValues["status"]) {
				let enabledFields = null;
				if (!isAddMode) {
					if (
						initRecordData.multiValues["status"].every(
							(x) => OrderStatusTransporterEditOrder_2.indexOf(x) !== -1
						)
					) {
					} else if (
						initRecordData.multiValues["status"].some(
							(x) => x === OrderStatus.ORDER_COMPLETED || x === OrderStatus.DELETED
						)
					) {
						enabledFields = {};
					} else {
						enabledFields = { notes: true, internal_notes: true };
					}
					setEnabledFields(enabledFields);
				}
			}
			if (
				isAddMode ||
				initRecordData.multiValues["status"].every(
					(x) => OrderStatusTransporterEditOrder_1.indexOf(x) !== -1
				)
			) {
				setLoadingOrderDisable(
					permissions.company_id !== values.etd_3rd_party ||
						(DriverRoles.indexOf(permissions.role) !== -1 && canEditOnlyTime)
				);
			}
		}
	}, [permissions, recordData]);

	React.useEffect(() => {
		setRecordData((prev) => {
			const diffKeys = getDiffKeys(prev.values, values);
			const newState = { ...prev.state };

			for (const key of diffKeys) {
				const isDiff = isMultiDiff(prev.initialValues[key], values[key], prev.state[key]);
				if (isDiff) newState[key] |= MultiChangesStateEnum.IS_DIFF;
				else newState[key] &= ~MultiChangesStateEnum.IS_DIFF;

				if (fieldRelation[key]) {
					fieldRelation[key].forEach((x) => {
						if (isDiff) newState[x] |= MultiChangesStateEnum.NOT_REVERT;
						else newState[x] &= ~MultiChangesStateEnum.NOT_REVERT;
					});
				}
			}

			const newObj = {
				...prev,
				state: newState,
				values,
			};
			return newObj;
		});
	}, [values]);

	React.useEffect(() => {
		setRecordData((prev) => ({
			...prev,
			initialValues,
		}));
	}, [initialValues]);

	if (!permissions) return null;

	const handleCbMulti = (type, key) => {
		switch (type) {
			case "revert":
				form.change(key, initialValues[key]);
				switch (key) {
					case "eta_3rd_party":
						handleCbMulti("revert", "eta_driver");
						handleCbMulti("revert", "eta_driver_id");
						handleCbMulti("revert", "eta_truck");
						handleCbMulti("revert", "eta_truck_id");
						handleCbMulti("revert", "eta_trailer");
						handleCbMulti("revert", "eta_trailer_id");
						break;
					case "etd_3rd_party":
						handleCbMulti("revert", "etd_driver");
						handleCbMulti("revert", "etd_driver_id");
						handleCbMulti("revert", "etd_truck");
						handleCbMulti("revert", "etd_truck_id");
						handleCbMulti("revert", "etd_trailer");
						handleCbMulti("revert", "etd_trailer_id");
						break;
					default:
						break;
				}
				break;
			case "clear":
				form.change(key, undefined);
				break;
			default:
				break;
		}
	};

	const multiData = { recordData: recordData, cbMulti: handleCbMulti };

	const requiredMulti = (value, values, opt) => {
		if (recordData.values && recordData.initialValues) {
			const key = opt["name"];
			const isDiff = isMultiDiff(recordData.initialValues[key], value, recordData.state[key]);

			if (isDiff) {
				if (isNullUndEmpty(value)) return "Required";
			}
		}
	};

	const requiredValidArr = [requiredMulti];

	return (
		<GridForm {...props} enabledFields={enabledFields} multiData={multiData}>
			<FormGroupGrid>
				<GridInput
					source="company_id"
					sm={2}
					component={CompanySelect}
					filter={{ no_transporters: true }}
					disabled
				/>
				<GridInput
					source="reference"
					sm={2}
					component={OrderReferenceInput}
					validate={requiredValidArr}
					disabled
				/>
				<GridInput
					source="service"
					sm={2}
					component={ServiceInput}
					validate={requiredValidArr}
					defaultValue={OrderService.reloadCarCar}
					disabled
					isCouldStorageVisible={isAddMode || isColdStorageService}
				/>
				<GridInput sm={3} disabled>
					<ReferenceInput source="status" reference="order-statuses" validate={requiredValidArr}>
						<SelectDisabledInput allowedIds={OrderNormalStatuses} optionText="name" />
					</ReferenceInput>
				</GridInput>
				<GridInput sm={3} component={YesNoInput} source="priority" defaultValue={false} disabled />
				<GridInput
					sm={1}
					component={YesNoInput}
					source="is_per"
					defaultValue={false}
					disabled
					hidden={
						isFieldHidden(HideableOrderField.IS_PER) ||
						!perEligibleServices.includes(values.service)
					}
				/>
			</FormGroupGrid>

			<FormGroupGrid xs={12}>
				<ETAETDBlock
					sm={12}
					md={12}
					lg={6}
					isAddMode={isAddMode}
					requiredMulti={requiredMulti}
					prefix="eta"
					canEditOnlyTime={canEditOnlyTime}
					title="ETA  —  ARRIVAL"
					permissions={permissions}
					{...props}
					enabledFields={enabledFields}
					multiData={multiData}
					isFieldHidden={isFieldHidden}
					loadingOrderDisable={loadingOrderDisable}
				/>
				{!isColdStorageService ? (
					<ETAETDBlock
						sm={12}
						md={12}
						lg={6}
						isAddMode={isAddMode}
						requiredMulti={requiredMulti}
						prefix="etd"
						canEditOnlyTime={canEditOnlyTime}
						title="ETD  —  DEPARTURE"
						permissions={permissions}
						{...props}
						enabledFields={enabledFields}
						multiData={multiData}
						isFieldHidden={isFieldHidden}
						loadingOrderDisable={loadingOrderDisable}
					/>
				) : (
					<ColdStorageSection
						sm={12}
						md={12}
						lg={6}
						isAddMode={isAddMode}
						isMultiChanges={true}
						{...props}
					/>
				)}
			</FormGroupGrid>

			{!isColdStorageService && (
				<>
					<FormGroupGrid
						xs={12}
						md={6}
						lg={4}
						title={translate("resources.orders.block-label.split-loads")}
						{...props}
						hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
					>
						<GridInput
							xs={4}
							component={YesNoInput}
							source="split_load"
							defaultValue={false}
							disabled
							hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
						/>
					</FormGroupGrid>
				</>
			)}

			<FormGroupGrid>
				<GridInput
					sm={12}
					md={6}
					component={TextInput}
					source="notes"
					multiline
					rows={6}
					validate={[maxLength(1024)]}
				/>
				<GridInput
					sm={12}
					md={6}
					className="red-border"
					component={DangerTextInputForInternalNotes}
					source="internal_notes"
					multiline
					rows={6}
					validate={[maxLength(500)]}
				/>
			</FormGroupGrid>
		</GridForm>
	);
};
